import { Box, Button, Typography } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import { FC, useRef } from "react";
import {
  LoadingButton,
  ModalComponent,
  EConfirmModalType,
  IModalComponentProps,
} from "src/shared/components";
import { addressTitles } from "src/shared/constants/address.constants";
import { modalText } from "src/shared/constants/form.constants";
import { useConfirmModal } from "src/shared/hooks";
import { IAddressFull, IAddressShort } from "src/shared/types/address.types";
import {
  useGetPartnesAddressesListQuery,
  usePostAddressMutation,
  usePutAddressMutation,
} from "../../model/api/partners";
import { getPostAddressDto } from "../../model/helpers/helpers";
import { AddressForm } from "./AddressForm";
interface IEditAddressModalProps extends IModalComponentProps {
  partnerId: string;
  address: IAddressFull;
  registrationCountryId: number;
}
export const EditAddressModal: FC<IEditAddressModalProps> = ({
  partnerId,
  registrationCountryId,
  address,
  ...modalCompnentProps
}) => {
  const formRef = useRef<HTMLFormElement>(null);
  const defaultValues: IAddressShort = {
    area: address.area,
    postcode: address.postcode,
    country_id: address.country.id,
    type: address.type.value,
    region: address.region,
    address: address.address,
    city: address.city,
    id: address.id,
    isPostAddressTheSame: false,
  };
  const [putAddress, { isLoading: isLoadingPut }] = usePutAddressMutation();
  const [postAddress, { isLoading: isLoadingPost }] = usePostAddressMutation();
  const { data: addresses, isLoading: isAddressesLoading } =
    useGetPartnesAddressesListQuery(
      address.type.value === "legal" ? { partner_id: partnerId } : skipToken,
    );

  const postalAddressId = addresses?.find(
    (address) => address.type.value === "postal",
  )?.id;

  const isLoading = isLoadingPost || isLoadingPut || isAddressesLoading;
  const handleSave = async (values: IAddressShort) => {
    const { isPostAddressTheSame, id, ...address } = values;
    try {
      await putAddress({
        partnerId,
        address: getPostAddressDto({ ...address, id }),
      }).unwrap();
      if (address.type === "legal" && isPostAddressTheSame) {
        if (postalAddressId) {
          await putAddress({
            partnerId,
            address: getPostAddressDto({
              ...address,
              id: postalAddressId,
              type: "postal",
            }),
          }).unwrap();
        } else {
          await postAddress({
            partnerId,
            address: getPostAddressDto({ ...address, type: "postal" }),
          }).unwrap();
        }
      }
      modalCompnentProps.onClose();
    } catch (e) {
      enqueueSnackbar("Произошла ошибка", {
        variant: "error",
        handleClose: closeSnackbar,
      });
      console.log(e);
    }
  };

  const { modal, showModal } = useConfirmModal({
    onResolve: modalCompnentProps.onClose,
    confirmModalType: EConfirmModalType.WARNING,
    confirmBtnProps: { isLoading: false, title: modalText.continue },
    children: <Typography>{modalText.unsavedChanges}</Typography>,
    title: modalText.confirmAction,
  });

  return (
    <>
      {modal}
      <ModalComponent
        {...modalCompnentProps}
        onClose={showModal}
        width={450}
        disableBodyPadding
        title={addressTitles[address.type.value]}
        modalFooter={
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 2,
            }}
          >
            <Button variant="outlined" onClick={showModal}>
              Отменить
            </Button>
            <LoadingButton
              isLoading={isLoading}
              variant="contained"
              onClick={() => {
                formRef.current?.submit();
              }}
            >
              Сохранить
            </LoadingButton>
          </Box>
        }
      >
        <AddressForm
          ref={formRef}
          handleSave={handleSave}
          address={defaultValues}
          registrationCountryId={registrationCountryId}
        />
      </ModalComponent>
    </>
  );
};
